import { CHARACTER_VISIBILITY } from "../../utils/sharedTypes";
import getCharacterLorebookProfileIndicator from "../lorebook/getCharacterLorebookProfileIndicator";
import { readCharacterId } from "./buildDiscoverChatbotCardViewModel";
import canBlockCharacterCreator from "./canBlockCharacterCreator";
import readDiscoverCharacterRatingScore from "./readDiscoverCharacterRatingScore";
import resolveLocalizedField from "./resolveLocalizedField";
const RATING_PERCENT_MULTIPLIER = 100;
function shouldBlurAvatar({ character, blurNSFW }) {
    return blurNSFW && character.avatar_is_nsfw;
}
function isCharacterOwner({ character, onlyUserId }) {
    return !!onlyUserId && character.creator_user_id === onlyUserId;
}
function isRecommendedCharacter(rowCharacter) {
    return Boolean(rowCharacter?.recommId);
}
function resolveStatus(profileViewModel) {
    if (profileViewModel.status === "ready") {
        return "ready";
    }
    if (profileViewModel.status === "error") {
        return "error";
    }
    if (profileViewModel.status === "not-found") {
        return "not-found";
    }
    return "loading";
}
function buildPreview({ preview, language, blurNSFW, }) {
    if (preview === null) {
        return null;
    }
    return {
        name: resolveLocalizedField({ localizedValues: preview.name_i18n, language, fallback: preview.name }),
        avatarUrl: preview.avatar_url,
        isAvatarBlurred: shouldBlurAvatar({ character: preview, blurNSFW }),
        isRecommended: isRecommendedCharacter(preview),
    };
}
function buildStats(character) {
    const ratingPercent = character.rating_score === undefined
        ? undefined
        : Math.round(readDiscoverCharacterRatingScore(character.rating_score) * RATING_PERCENT_MULTIPLIER);
    return { messageCount: character.num_messages, ratingPercent, tokenCount: character.token_count };
}
function buildLorebook({ character, onlyUserId, isLorebookIndicatorEnabled, }) {
    if (!isLorebookIndicatorEnabled) {
        return null;
    }
    return getCharacterLorebookProfileIndicator({ character, isCreator: isCharacterOwner({ character, onlyUserId }) });
}
// Profile fields arrive translated from the endpoint; the i18n lookup is only for the row preview.
function buildProfile({ character, onlyUserId, blurNSFW, isRecommended, isLorebookIndicatorEnabled, }) {
    const isDefinitionVisible = character.definition_visible || isCharacterOwner({ character, onlyUserId });
    return {
        characterId: readCharacterId(character),
        name: character.name,
        description: character.title || undefined,
        greeting: character.greeting || undefined,
        personality: isDefinitionVisible ? character.persona || undefined : undefined,
        scenario: isDefinitionVisible ? character.scenario || undefined : undefined,
        exampleDialogue: isDefinitionVisible ? character.dialogue || undefined : undefined,
        avatarUrl: character.avatar_url,
        isAvatarBlurred: shouldBlurAvatar({ character, blurNSFW }),
        isRecommended,
        creatorUsername: character.creator_username || undefined,
        tags: character.tags ?? [],
        stats: buildStats(character),
        lorebook: buildLorebook({ character, onlyUserId, isLorebookIndicatorEnabled }),
    };
}
// Moderation and suggestions need a signed-in viewer and a loaded profile; creators cannot block themselves.
function buildViewerPermissions({ character, onlyUserId }) {
    if (character === null || !onlyUserId) {
        return { canBlockCreator: false, canReport: false, canLessLikeThis: false, canSuggestTag: false };
    }
    return {
        canBlockCreator: canBlockCharacterCreator({ character, onlyUserId }),
        canReport: true,
        canLessLikeThis: true,
        canSuggestTag: true,
    };
}
/** Guests and signed-in viewers get the same data; the host decides what a guest tap on favorite does. */
export default function buildDiscoverChatbotProfileOverlayViewProps({ isOpen, characterId, preview, profileViewModel, language, onlyUserId, blurNSFW, likedBots, optimisticFavorite = null, isFavoritePending = false, isShareAllowed, isLorebookIndicatorEnabled, labels, callbacks, }) {
    const character = profileViewModel.status === "ready" ? profileViewModel.character : null;
    const status = resolveStatus(profileViewModel);
    const isRecommended = isRecommendedCharacter(preview);
    const profile = character === null ? null : buildProfile({ character, onlyUserId, blurNSFW, isRecommended, isLorebookIndicatorEnabled });
    const lorebook = profile?.lorebook ?? null;
    const { onLorebookPress, ...viewCallbacks } = callbacks;
    return {
        isOpen,
        status,
        preview: buildPreview({ preview, language, blurNSFW }),
        profile,
        canChat: characterId !== null && (status === "loading" || status === "ready"),
        canFavorite: character !== null && character.visibility === CHARACTER_VISIBILITY.PUBLIC,
        isFavorite: optimisticFavorite ?? (characterId !== null && likedBots.includes(characterId)),
        isFavoritePending,
        canShare: isShareAllowed,
        canOpenCreator: character !== null && !!character.creator_username,
        ...buildViewerPermissions({ character, onlyUserId }),
        labels,
        ...viewCallbacks,
        onLorebookPress: () => {
            if (lorebook?.canNavigate && lorebook.id) {
                onLorebookPress(lorebook.id);
            }
        },
    };
}
